import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { type RootState } from '../store'
import {
  type CrossClientQueryDecompositionResult,
  type CrossClientQueryExecution,
  type CrossClientQueryExecutionProgress,
  CrossClientQueryPhases,
} from '../../models/CrossClient'
import { type User } from '../../models/User'
import { executionIsRunning } from './utils'

interface CrossClientLandingState {
  initLoading: boolean
  evaluateLoading: boolean
  decompositionResult: CrossClientQueryDecompositionResult | null
  runningExecutions: CrossClientQueryExecution[]
  runningExecutionProgresses: CrossClientQueryExecutionProgress[]
  runningExecutionsLoading: boolean
  finishedExecutions: CrossClientQueryExecution[]
  totalFinishedExecutions: number
  finishedExecutionProgresses: CrossClientQueryExecutionProgress[]
  finishedExecutionsLoading: boolean
  finishedExecutionsLoadingMore: boolean
  pollingEnabled: boolean
  teammates: User[]
  teammatesLoading: boolean
}

const initialState: CrossClientLandingState = {
  initLoading: false,
  evaluateLoading: false,
  decompositionResult: null,
  runningExecutions: [],
  runningExecutionProgresses: [],
  runningExecutionsLoading: true,
  finishedExecutions: [],
  totalFinishedExecutions: 0,
  finishedExecutionProgresses: [],
  finishedExecutionsLoading: true,
  finishedExecutionsLoadingMore: false,
  pollingEnabled: false,
  teammates: [],
  teammatesLoading: true,
}

export const crossClientLandingSlice = createSlice({
  name: 'crossClientLanding',
  initialState,
  reducers: {
    setCrossClientInitLoading: (state, action: PayloadAction<boolean>) => {
      state.initLoading = action.payload
    },
    setCrossClientEvaluateLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluateLoading = action.payload
    },

    setCrossClientLandingDecompositionResult: (state, action: PayloadAction<CrossClientQueryDecompositionResult | null>) => {
      state.decompositionResult = action.payload
    },
    addCrossClientLandingFinishedExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      const newExecutions = action.payload.filter(
        (e) =>
          !state.finishedExecutions.some((finishedExecution) => finishedExecution.id === e.id) &&
          !state.runningExecutions.some((runningExecution) => runningExecution.id === e.id),
      )
      state.finishedExecutions.push(...newExecutions)
    },
    prependCrossClientLandingFinishedExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      const newExecutions: CrossClientQueryExecution[] = []
      for (const serverExec of action.payload) {
        if (state.runningExecutions.some((e) => e.id === serverExec.id)) {
          continue
        }
        const idx = state.finishedExecutions.findIndex((e) => e.id === serverExec.id)
        if (idx === -1) {
          newExecutions.push(serverExec)
        } else {
          state.finishedExecutions[idx] = { ...state.finishedExecutions[idx], ...serverExec }
        }
      }
      state.finishedExecutions.unshift(...newExecutions)
    },
    deleteCrossClientLandingExecution: (state, action: PayloadAction<CrossClientQueryExecution>) => {
      state.runningExecutions = state.runningExecutions.filter((e) => e.id !== action.payload.id)
      state.runningExecutionProgresses = state.runningExecutionProgresses.filter((p) => p.executionId !== action.payload.id)
    },
    stopCrossClientLandingExecution: (state, action: PayloadAction<CrossClientQueryExecution>) => {
      state.runningExecutions = state.runningExecutions.filter((e) => e.id !== action.payload.id)
      state.runningExecutionProgresses = state.runningExecutionProgresses.filter((p) => p.executionId !== action.payload.id)
      const canceledExecution: CrossClientQueryExecution = {
        ...action.payload,
        phase: CrossClientQueryPhases.CANCELED,
      }
      if (!state.finishedExecutions.some((e) => e.id === canceledExecution.id)) {
        state.finishedExecutions.unshift(canceledExecution)
      }
    },
    addCrossClientLandingFinishedExecutionProgresses: (state, action: PayloadAction<CrossClientQueryExecutionProgress[]>) => {
      state.finishedExecutionProgresses.push(...action.payload)
    },
    setCrossClientLandingFinishedExecutionsLoading: (state, action: PayloadAction<boolean>) => {
      state.finishedExecutionsLoading = action.payload
    },
    setCrossClientLandingFinishedExecutionsLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.finishedExecutionsLoadingMore = action.payload
    },
    setCrossClientLandingTotalFinishedExecutions: (state, action: PayloadAction<number>) => {
      state.totalFinishedExecutions = action.payload
    },
    setCrossClientLandingRunningExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      state.runningExecutions = action.payload
    },
    setCrossClientLandingRunningExecutionProgresses: (state, action: PayloadAction<CrossClientQueryExecutionProgress[]>) => {
      const stillRunningProgresses = action.payload.filter(executionIsRunning)
      const finishedProgresses = action.payload.filter((e) => !executionIsRunning(e))
      state.runningExecutionProgresses = stillRunningProgresses
      for (const progress of finishedProgresses) {
        const idx = state.runningExecutions.findIndex((e) => e.id === progress.executionId)
        if (idx !== -1) {
          const execution = state.runningExecutions[idx]
          execution.phase = progress.phase
          execution.updatedAt = progress.lastUpdatedAt
          state.runningExecutions.splice(idx, 1)
          if (!state.finishedExecutions.some((e) => e.id === execution.id)) {
            state.finishedExecutions.unshift(execution)
          }
        }
        if (!state.finishedExecutionProgresses.some((p) => p.executionId === progress.executionId)) {
          state.finishedExecutionProgresses.push(progress)
        }
      }
    },
    setCrossClientLandingRunningExecutionsLoading: (state, action: PayloadAction<boolean>) => {
      state.runningExecutionsLoading = action.payload
    },

    startCrossClientLandingPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientLandingPolling: (state) => {
      state.pollingEnabled = false
    },
    setCrossClientLandingTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    setCrossClientLandingTeammatesLoading: (state, action: PayloadAction<boolean>) => {
      state.teammatesLoading = action.payload
    },
    resetCrossClientLanding: () => initialState,
  },
})

export const {
  setCrossClientInitLoading,
  setCrossClientEvaluateLoading,
  addCrossClientLandingFinishedExecutions,
  prependCrossClientLandingFinishedExecutions,
  addCrossClientLandingFinishedExecutionProgresses,
  setCrossClientLandingDecompositionResult,
  startCrossClientLandingPolling,
  stopCrossClientLandingPolling,
  setCrossClientLandingRunningExecutions,
  setCrossClientLandingRunningExecutionProgresses,
  setCrossClientLandingRunningExecutionsLoading,
  setCrossClientLandingFinishedExecutionsLoading,
  setCrossClientLandingFinishedExecutionsLoadingMore,
  stopCrossClientLandingExecution,
  deleteCrossClientLandingExecution,
  setCrossClientLandingTotalFinishedExecutions,
  setCrossClientLandingTeammates,
  setCrossClientLandingTeammatesLoading,
  resetCrossClientLanding,
} = crossClientLandingSlice.actions

export const selectCrossClientInitLoading = (state: RootState) => state.crossClientLanding.initLoading

export const selectCrossClientEvaluateLoading = (state: RootState) => state.crossClientLanding.evaluateLoading

export const selectCrossClientLandingDecompositionResult = (state: RootState) => state.crossClientLanding.decompositionResult

export const selectCrossClientLandingPollingEnabled = (state: RootState) => state.crossClientLanding.pollingEnabled

export const selectCrossClientLandingFinishedExecutions = (state: RootState) => state.crossClientLanding.finishedExecutions

export const selectCrossClientLandingFinishedExecutionProgresses = (state: RootState) => state.crossClientLanding.finishedExecutionProgresses

export const selectCrossClientLandingRunningExecutions = (state: RootState) => state.crossClientLanding.runningExecutions

export const selectCrossClientLandingQueuedExecutions = createSelector([selectCrossClientLandingRunningExecutions], (runningExecutions) =>
  runningExecutions.filter((e) => e.phase === CrossClientQueryPhases.QUEUED),
)

export const selectCrossClientLandingProcessingExecutions = createSelector([selectCrossClientLandingRunningExecutions], (runningExecutions) =>
  runningExecutions.filter((e) => e.phase === CrossClientQueryPhases.PROCESSING),
)

export const selectCrossClientLandingRunningExecutionProgresses = (state: RootState) => state.crossClientLanding.runningExecutionProgresses

export const selectCrossClientLandingFinishedExecutionsLoading = (state: RootState) => state.crossClientLanding.finishedExecutionsLoading

export const selectCrossClientLandingFinishedExecutionsLoadingMore = (state: RootState) => state.crossClientLanding.finishedExecutionsLoadingMore

export const selectCrossClientLandingRunningExecutionsLoading = (state: RootState) => state.crossClientLanding.runningExecutionsLoading

export const selectCrossClientLandingTotalFinishedExecutions = (state: RootState) => state.crossClientLanding.totalFinishedExecutions

export const selectCrossClientLandingTeammates = (state: RootState) => state.crossClientLanding.teammates

export const selectCrossClientLandingTeammatesLoading = (state: RootState) => state.crossClientLanding.teammatesLoading

export default crossClientLandingSlice.reducer
